import { motion } from 'framer-motion';
import { Calendar, MapPin, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import { typography } from '../../../styles/typography';
import { Card } from '../../ui';
import Input from '../../ui/input.js';
import { stockInApi } from './stockInApi.js';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Helper to get dates in current month
const getCurrentMonthDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day1 = new Date(year, month, 15).toISOString().split('T')[0];
  const day2 = new Date(year, month, 16).toISOString().split('T')[0];
  const day3 = new Date(year, month, 17).toISOString().split('T')[0];
  return { day1, day2, day3 };
};

// Dummy sites data
const dummySites = [
  { site_id: 'site_001', site_name: 'Bangalore Main Office' },
  { site_id: 'site_002', site_name: 'Mumbai Branch' },
  { site_id: 'site_003', site_name: 'Delhi Office' },
];

// Generate dummy stock-in data with current month dates
const getDummyStockInData = () => {
  const { day1, day2, day3 } = getCurrentMonthDates();
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 10).toISOString().split('T')[0];
  const nextMonth2 = new Date(now.getFullYear(), now.getMonth() + 2, 5).toISOString().split('T')[0];
  const nextMonth3 = new Date(now.getFullYear(), now.getMonth() + 3, 18).toISOString().split('T')[0];
  const nextMonth4 = new Date(now.getFullYear(), now.getMonth() + 1, 30).toISOString().split('T')[0];
  const nextMonth5 = new Date(now.getFullYear(), now.getMonth() + 4, 12).toISOString().split('T')[0];

  return [
    {
      id: 1,
      stockName: 'Wheat Flour',
      quantityOrWeight: 120,
      pricePerUnit: 32.5,
      totalPrice: 3900,
      expDate: nextMonth,
      vendorName: 'ABC Suppliers',
      date: day1,
      siteId: 'site_001',
      siteName: 'Bangalore Main Office',
    },
    {
      id: 2,
      stockName: 'Cooking Oil',
      quantityOrWeight: 80,
      pricePerUnit: 110,
      totalPrice: 8800,
      expDate: nextMonth2,
      vendorName: 'XYZ Distributors',
      date: day1,
      siteId: 'site_001',
      siteName: 'Bangalore Main Office',
    },
    {
      id: 3,
      stockName: 'Rice',
      quantityOrWeight: 150,
      pricePerUnit: 45,
      totalPrice: 6750,
      expDate: nextMonth3,
      vendorName: 'Prime Materials Ltd.',
      date: day2,
      siteId: 'site_002',
      siteName: 'Mumbai Branch',
    },
    {
      id: 4,
      stockName: 'Sugar',
      quantityOrWeight: 60,
      pricePerUnit: 38,
      totalPrice: 2280,
      expDate: nextMonth4,
      vendorName: 'Quality Goods Inc.',
      date: day3,
      siteId: 'site_002',
      siteName: 'Mumbai Branch',
    },
    {
      id: 5,
      stockName: 'Tea Powder',
      quantityOrWeight: 25,
      pricePerUnit: 210,
      totalPrice: 5250,
      expDate: nextMonth5,
      vendorName: 'Reliable Vendors',
      date: day3,
      siteId: 'site_003',
      siteName: 'Delhi Office',
    },
  ];
};

const dummyStockInData = getDummyStockInData();

const StockInView = () => {
  // Log screen view
  useScreenLogging('StockInView', {
    component: 'StockInView',
    screenType: 'view-stock-in',
  });

  const navigate = useNavigate();
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [sites, setSites] = useState(dummySites);
  const [stockInData, setStockInData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  // Load sites on component mount
  useEffect(() => {
    const loadSites = async () => {
      setLoadingSites(true);
      try {
        const sitesData = await stockInApi.getSites();
        const finalSites = sitesData && sitesData.length > 0 ? sitesData : dummySites;
        
        // Sort sites alphabetically by site name
        const sortedSites = [...finalSites].sort((a, b) => {
          const nameA = (a.site_name || a.name || '').toLowerCase();
          const nameB = (b.site_name || b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setSites(sortedSites);
        
        // Don't auto-select - let user select from dropdown
      } catch (err) {
        console.error('Error loading sites:', err);
        // Fallback to dummy sites on error
        // Sort dummy sites alphabetically
        const sortedDummySites = [...dummySites].sort((a, b) => {
          const nameA = (a.site_name || a.name || '').toLowerCase();
          const nameB = (b.site_name || b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setSites(sortedDummySites);
        // Don't auto-select - let user select from dropdown
      } finally {
        setLoadingSites(false);
      }
    };

    loadSites();
  }, []);

  // Fetch stock-in data when site and month are selected
  useEffect(() => {
    const fetchStockInData = async () => {
      if (!selectedSite || !selectedMonth) {
        setStockInData([]);
        setSummary(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const siteId = selectedSite.site_id || selectedSite.id;
        const result = await stockInApi.getStockInView({
          site_id: siteId,
          month: selectedMonth,
          limit: 500, // Max limit as per API spec
        });

        if (result.success) {
          // Map API response to component format
          const mappedData = result.stock_in_records.map((record) => ({
            id: record.ledger_id,
            ledger_id: record.ledger_id, // Include ledger_id for editing
            stock_item_id: record.stock_item_id,
            stockName: record.item_name || '',
            quantityOrWeight: record.quantity || 0,
            pricePerUnit: record.unit_cost || 0,
            totalPrice: record.total_cost || (record.quantity * record.unit_cost) || 0,
            vendor_id: record.vendor_id || '',
            vendorName: record.vendor_name || '',
            expDate: record.exp_date || record.expiry_date || '', // Prefer exp_date
            date: record.stock_date || record.event_date?.split('T')[0] || '',
            siteId: record.site_id || siteId,
            siteName: record.site_name || selectedSite.site_name || selectedSite.name || '',
            unit_of_measure: record.unit_of_measure || '',
            reference_id: record.reference_id || '',
            notes: record.notes || '',
          }));

          setStockInData(mappedData);
          setSummary(result.summary);
        } else {
          setError(result.message || 'Failed to fetch stock-in data');
          setStockInData([]);
          setSummary(null);
        }
      } catch (err) {
        console.error('Error fetching stock-in data:', err);
        setError(err.message || 'Failed to fetch stock-in data');
        setStockInData([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStockInData();
  }, [selectedSite, selectedMonth]);

  // Use API data instead of filtering dummy data
  const filteredRows = useMemo(() => {
    if (!selectedSite) return [];
    return stockInData;
  }, [stockInData, selectedSite]);

  // Group filtered rows by date
  const groupedByDate = useMemo(() => {
    const grouped = {};
    filteredRows.forEach((row) => {
      const date = row.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(row);
    });
    return grouped;
  }, [filteredRows]);

  // Get dates sorted
  const dates = useMemo(() => {
    return Object.keys(groupedByDate).sort((a, b) => new Date(b) - new Date(a));
  }, [groupedByDate]);

  // Calculate totals for a date
  const getDateTotals = (date) => {
    const items = groupedByDate[date] || [];
    const totalItems = items.length;
    const totalCost = items.reduce((sum, item) => {
      return sum + (item.totalPrice || ((item.quantityOrWeight || 0) * (item.pricePerUnit || 0)));
    }, 0);
    return { totalItems, totalCost };
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="w-full h-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header with Title and Create Button */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={typography.h2} style={{ color: '#000000' }}>
                Stock In
              </h1>
              <p className="mt-2 text-gray-600">
                View and manage stock-in entries
              </p>
            </div>
            <button
              onClick={() => navigate('/onboarding-stock-in/create')}
              className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              <Plus className="w-4 h-4" />
              Create Stock In
            </button>
          </div>

          <div className="rounded-3xl">
            {/* Site and Month Selection */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div className="min-w-[200px]">
                <label htmlFor="stock-in-site" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <MapPin className="w-4 h-4" />
                  Select Site
                </label>
                <select
                  id="stock-in-site"
                  value={selectedSite ? (selectedSite.site_id || selectedSite.id) : ''}
                  onChange={(e) => {
                    const siteId = e.target.value;
                    const site = sites.find(s => (s.site_id || s.id) === siteId);
                    setSelectedSite(site || null);
                  }}
                  disabled={loadingSites}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                >
                  <option value="">
                    {loadingSites ? 'Loading sites...' : 'Select a site'}
                  </option>
                  {/* alphabetical order */}
                  {Array.isArray(sites) && sites.map((site) => (
                    <option key={site.site_id || site.id} value={site.site_id || site.id}>
                      {site.site_name || site.name || 'Unknown Site'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[200px]">
                <label htmlFor="stock-in-month" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <Calendar className="w-4 h-4" />
                  Select Month
                </label>
                <Input
                  id="stock-in-month"
                  type="month"
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(event.target.value)}
                  className="w-full h-12 rounded-2xl border bg-white pr-4 text-sm focus:outline-none focus:ring-2 border-gray-200 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <Card.Light className="p-8 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  <p className="text-gray-500">Loading stock-in entries...</p>
                </div>
              </Card.Light>
            )}

            {/* Error State */}
            {error && !loading && (
              <Card.Light className="p-8 text-center">
                <p className="text-red-600 text-lg">{error}</p>
              </Card.Light>
            )}

            {/* Stock In Cards - Grouped by Date */}
            {!loading && !error && (
              <div className="space-y-4">
                {dates.length > 0 ? (
                  <>
                    {/* Summary Card (if available) */}
                    {summary && (
                      <Card.Light className="p-6 bg-green-50 border-green-200">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600 block">Total Records</span>
                            <span className="font-semibold text-lg">{summary.total_records || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-600 block">Total Quantity</span>
                            <span className="font-semibold text-lg">{summary.total_quantity?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600 block">Total Value</span>
                            <span className="font-semibold text-lg text-green-600">
                              ₹{summary.total_value?.toFixed(2) || '0.00'}
                            </span>
                          </div>
                          {/* <div>
                            <span className="text-gray-600 block">Site</span>
                            <span className="font-semibold text-lg">{summary.site_name || ''}</span>
                          </div> */}
                        </div>
                      </Card.Light>
                    )}

                    {dates.map((date, dateIndex) => {
                      const { totalItems, totalCost } = getDateTotals(date);
                      const itemsForDate = groupedByDate[date] || [];
                      
                      return (
                        <motion.div
                          key={date}
                          variants={cardVariants}
                          initial="hidden"
                          animate="visible"
                        >
                          <Card.Light
                            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                            onClick={() => {
                              // Navigate to create stock in form with all items for this date pre-filled
                              navigate('/onboarding-stock-in/create', {
                                state: {
                                  date: date,
                                  siteId: itemsForDate[0]?.siteId,
                                  siteName: itemsForDate[0]?.siteName,
                                  items: itemsForDate.map(row => ({
                                    id: row.id,
                                    ledger_id: row.ledger_id || row.id, // Pass ledger_id for editing
                                    stock_item_id: row.stock_item_id,
                                    stockName: row.stockName || row.itemName || row.stock_name || row.item_name || '',
                                    quantityOrWeight: row.quantityOrWeight || row.quantity || '',
                                    pricePerUnit: row.pricePerUnit || row.pricePerKg || row.price_per_unit || '',
                                    totalPrice: row.totalPrice || ((row.quantityOrWeight || row.quantity || 0) * (row.pricePerUnit || row.pricePerKg || row.price_per_unit || 0)) || '',
                                    vendor_id: row.vendor_id || '',
                                    vendorName: row.vendorName || row.vendor_name || '',
                                    expDate: row.expDate || row.exp_date || row.expiry_date || '', // Prefer exp_date
                                    reference_id: row.reference_id || '',
                                    notes: row.notes || '',
                                  })),
                                  editMode: true, // Enable editing mode
                                }
                              });
                            }}
                          >
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  {/* <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">
                                    {dateIndex + 1}
                                  </div> */}
                                  <h3 className="text-xl font-semibold text-gray-800">
                                    {new Date(date).toLocaleDateString('en-US', { 
                                      weekday: 'long', 
                                      year: 'numeric', 
                                      month: 'long', 
                                      day: 'numeric' 
                                    })}
                                  </h3>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <span className="text-gray-600">Total Items: </span>
                                    <span className="font-medium">{totalItems}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Total Cost: </span>
                                    <span className="font-medium text-green-600">
                                      ₹{totalCost.toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Card.Light>
                        </motion.div>
                      );
                    })}
                  </>
                ) : (
                  <Card.Light className="p-8 text-center">
                    <p className="text-gray-500 text-lg">
                      {!selectedSite
                        ? 'Please select a site to view stock-in entries.'
                        : 'No stock-in entries found for the selected filters.'}
                    </p>
                  </Card.Light>
                )}
              </div>
            )}

          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default StockInView;
